export const COLLABORATION_MILESTONE_DECISION_STATES = [
  "ACTION REQUIRED",
  "CONDITIONALLY READY",
  "MILESTONE READY",
  "NOT READY",
];

export const COLLABORATION_MILESTONE_FINDING_TYPES = [
  "GATING ISSUE",
  "EVIDENCE GAP",
  "GOVERNANCE DECISION",
  "CLARIFICATION",
  "CLEAR",
];

const defaultFinding = () => ({
  id: "",
  type: "EVIDENCE GAP",
  severity: "Medium",
  owner: "",
  title: "",
  explanation: "",
  gateRequirement: "",
  agreementEvidenceSource: "",
  clauseReference: "",
  confidence: "Medium",
  availableEvidence: "",
  evidenceSource: "",
  whyItMatters: "",
  closureRequirement: "",
  status: "Unresolved",
});

const defaultAction = () => ({
  owner: "",
  dueLabel: "",
  status: "Open",
  action: "",
  linkedFinding: "",
});

const defaultHistoryEntry = () => ({
  date: "",
  state: "ACTION REQUIRED",
  owner: "",
  origin: "",
  note: "",
});

export function createEmptyCollaborationContext() {
  return {
    profileId: "",
    organisationName: "",
    organisationShortName: "",
    organisationType: "Biotechnology",
    legalEntity: "",
    website: "",
    location: "",
    partnerName: "",
    partnerShortName: "",
    programmeName: "",
    therapeuticArea: "",
    collaborationType: "Collaboration and licence",
    agreementDate: "",
    legalEffectiveDate: "",
    disclosedEconomics: "",
    responsibilityModel: "",
    currentProgrammeStage: "",
    targetMilestone: "",
    targetGateDate: "",
    relevantCollaborations: "",
    keyFunctionsInvolved: "",
    governanceControls: "",
    strengths: "",
    unknowns: "",
  };
}

export function createEmptyMilestoneDefinition() {
  return {
    milestoneName: "",
    milestoneDescription: "",
    agreementSources: "",
    operativeMilestoneRequirement: "",
    relevantClauses: "",
    requiredEvidencePackage: "",
    governanceApprovalRequirement: "",
    responsibilityTransition: "",
    contractualTrigger: "",
    noticeRequirements: "",
    paymentImplication: "",
    ipRequirements: "",
    handoffDeliverables: "",
    assumptionsLimitations: "",
    milestoneSummary: "",
  };
}

export function createEmptyMilestoneDecisionDraft() {
  return {
    brand: "Layerbound",
    badge: "Manual milestone gatecheck",
    status: "ACTION REQUIRED",
    confidence: "Medium",
    decisionOwner: "",
    recommendation: "Do not declare this milestone yet.",
    humanDecision: "",
    approvalStatus: "",
    rationale: "",
    opportunitySummary: "",
    overrideReason: "",
    lastUpdated: new Date().toISOString(),
    findings: [defaultFinding()],
    nextActions: [defaultAction()],
    decisionHistory: [defaultHistoryEntry()],
  };
}

function makeId(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function summariseExecutiveCounts(findings) {
  const counts = findings.reduce(
    (acc, finding) => {
      const type = String(finding.type || "").toUpperCase();
      if (type === "GATING ISSUE") acc.gatingIssues += 1;
      if (type === "EVIDENCE GAP") acc.evidenceGaps += 1;
      if (type === "GOVERNANCE DECISION") acc.governanceDecisions += 1;
      return acc;
    },
    { gatingIssues: 0, evidenceGaps: 0, governanceDecisions: 0 }
  );

  return [
    { label: "Gating issues", value: counts.gatingIssues },
    { label: "Evidence gaps", value: counts.evidenceGaps },
    { label: "Governance decisions", value: counts.governanceDecisions },
  ];
}

export function buildCollaborationMilestoneGatecheckRecord(
  collaborationContext,
  milestoneDefinition,
  decisionDraft
) {
  const findings = (decisionDraft.findings || [])
    .map((finding, index) => ({
      ...defaultFinding(),
      ...finding,
      id: finding.id || makeId(finding.title, `finding-${index + 1}`),
    }))
    .filter((finding) => finding.title.trim());
  const nextActions = (decisionDraft.nextActions || [])
    .map((action) => ({ ...defaultAction(), ...action }))
    .filter((action) => action.action.trim());
  const decisionHistory = (decisionDraft.decisionHistory || [])
    .map((entry) => ({ ...defaultHistoryEntry(), ...entry }))
    .filter((entry) => entry.date || entry.note.trim() || entry.owner.trim());

  return {
    brand: decisionDraft.brand || "Layerbound",
    badge: decisionDraft.badge || "Manual milestone gatecheck",
    organisationName: collaborationContext.organisationName,
    organisationShortName:
      collaborationContext.organisationShortName ||
      collaborationContext.organisationName,
    organisationType: collaborationContext.organisationType || "",
    legalEntity: collaborationContext.legalEntity || "",
    website: collaborationContext.website || "",
    location: collaborationContext.location || "",
    partnerName: collaborationContext.partnerName || "",
    partnerShortName: collaborationContext.partnerShortName || "",
    programmeName: collaborationContext.programmeName || "",
    therapeuticArea: collaborationContext.therapeuticArea || "",
    collaborationType: collaborationContext.collaborationType || "",
    agreementDate: collaborationContext.agreementDate || "",
    legalEffectiveDate: collaborationContext.legalEffectiveDate || "",
    disclosedEconomics: collaborationContext.disclosedEconomics || "",
    responsibilityModel: collaborationContext.responsibilityModel || "",
    currentProgrammeStage: collaborationContext.currentProgrammeStage || "",
    targetMilestone: collaborationContext.targetMilestone || "",
    targetGateDate: collaborationContext.targetGateDate || "",
    relevantCollaborations: collaborationContext.relevantCollaborations || "",
    keyFunctionsInvolved: collaborationContext.keyFunctionsInvolved || "",
    governanceControls: collaborationContext.governanceControls || "",
    strengths: collaborationContext.strengths || "",
    unknowns: collaborationContext.unknowns || "",
    milestoneName: milestoneDefinition.milestoneName || "",
    milestoneDescription: milestoneDefinition.milestoneDescription || "",
    agreementSources: milestoneDefinition.agreementSources || "",
    operativeMilestoneRequirement:
      milestoneDefinition.operativeMilestoneRequirement || "",
    relevantClauses: milestoneDefinition.relevantClauses || "",
    requiredEvidencePackage:
      milestoneDefinition.requiredEvidencePackage || "",
    governanceApprovalRequirement:
      milestoneDefinition.governanceApprovalRequirement || "",
    responsibilityTransition:
      milestoneDefinition.responsibilityTransition || "",
    contractualTrigger: milestoneDefinition.contractualTrigger || "",
    noticeRequirements: milestoneDefinition.noticeRequirements || "",
    paymentImplication: milestoneDefinition.paymentImplication || "",
    ipRequirements: milestoneDefinition.ipRequirements || "",
    handoffDeliverables: milestoneDefinition.handoffDeliverables || "",
    assumptionsLimitations: milestoneDefinition.assumptionsLimitations || "",
    milestoneSummary:
      milestoneDefinition.milestoneSummary ||
      decisionDraft.opportunitySummary ||
      milestoneDefinition.milestoneDescription ||
      "",
    lastUpdated: decisionDraft.lastUpdated || new Date().toISOString(),
    decision: {
      status: decisionDraft.status || "ACTION REQUIRED",
      recommendation:
        decisionDraft.recommendation || "Do not declare this milestone yet.",
      rationale: decisionDraft.rationale || "",
      confidence: decisionDraft.confidence || "",
    },
    decisionOwnership: {
      humanDecision: decisionDraft.humanDecision || "",
      decisionOwner: decisionDraft.decisionOwner || "",
      approvalStatus: decisionDraft.approvalStatus || "",
      overrideReason: decisionDraft.overrideReason || "",
    },
    findings,
    nextActions,
    decisionHistory,
    executiveSummary: summariseExecutiveCounts(findings),
  };
}

export function buildCollaborationMilestoneTemplateMeta(record) {
  const decision = record?.decision || {};
  return {
    flowType: "collaboration_milestone_gatecheck",
    brandName: record?.brand || "Layerbound",
    headline: `${record?.milestoneName || "Milestone Gatecheck"} — ${record?.programmeName || "Collaboration milestone"}`,
    metaDescription:
      decision.recommendation ||
      "Live decision record for collaboration milestone readiness.",
    collaborationMilestoneGatecheckRecord: record,
  };
}

export function validateCollaborationMilestoneWorkspace(
  collaborationContext,
  milestoneDefinition,
  decisionDraft
) {
  const errors = [];
  if (!collaborationContext.organisationName.trim()) {
    errors.push("Organisation is required.");
  }
  if (!collaborationContext.partnerName.trim()) {
    errors.push("Partner / counterparty is required.");
  }
  if (!collaborationContext.programmeName.trim()) {
    errors.push("Programme / asset is required.");
  }
  if (!milestoneDefinition.milestoneName.trim()) {
    errors.push("Milestone name is required.");
  }
  if (!milestoneDefinition.operativeMilestoneRequirement.trim()) {
    errors.push("Operative milestone requirement is required.");
  }
  if (!decisionDraft.status.trim()) {
    errors.push("Decision status is required.");
  }
  if (!decisionDraft.recommendation.trim()) {
    errors.push("Recommendation is required.");
  }
  const hasFinding = (decisionDraft.findings || []).some((finding) =>
    String(finding.title || "").trim()
  );
  if (!hasFinding) {
    errors.push("Add at least one finding.");
  }
  const hasAction = (decisionDraft.nextActions || []).some((action) =>
    String(action.action || "").trim()
  );
  if (!hasAction) {
    errors.push("Add at least one next action.");
  }
  return errors;
}

export function getCollaborationMilestonePresetOptions() {
  return [];
}

export function createCollaborationMilestoneBlankWorkspace() {
  return {
    collaborationContext: createEmptyCollaborationContext(),
    milestoneDefinition: createEmptyMilestoneDefinition(),
    decisionDraft: createEmptyMilestoneDecisionDraft(),
    storeSlug: "milestone-gatecheck",
  };
}

export function duplicateMilestoneFinding() {
  return defaultFinding();
}

export function duplicateMilestoneAction() {
  return defaultAction();
}

export function duplicateMilestoneHistoryEntry() {
  return defaultHistoryEntry();
}
